import * as React from 'react';
import {
  Box,
  Button,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

interface ConditionalActionEmptyStateProps {
  titleKey: string;
  /** From x-entity-presentation.emptyStateHint */
  emptyStateHintKey?: string;
  /** From rules.x-entity-presentation.arrayAddLabel */
  addRuleLabelKey?: string;
  /**
   * Shared with the rule-list Add-rule button (the two are mutually
   * exclusive renders) so post-delete focus management has one stable
   * target either way.
   */
  addRuleButtonId?: string;
  onAddRule: () => void;
  enabled: boolean;
  /**
   * Which action this config offers. Controls the linear-next footer note,
   * which only applies to the `goTo` (navigate) config — for `show`
   * (visibility), no rules means "always shown," not "linear next."
   * Defaults to `goTo` so the note shows for unqualified callers.
   */
  actionType?: 'goTo' | 'show';
}

export const ConditionalActionEmptyState: React.FC<
  ConditionalActionEmptyStateProps
> = ({
  titleKey,
  emptyStateHintKey,
  addRuleLabelKey,
  addRuleButtonId,
  onAddRule,
  enabled,
  actionType = 'goTo',
}) => {
  const { formatMessage } = useIntl();
  return (
    <StackLayout
      horizontalAlignment="center"
      verticalAlignment="center"
      spacing="$one"
      css={{
        backgroundColor: '$surfaceBackgroundSecondary',
        border: 'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
        borderRadius: '$medium',
        paddingY: '$two',
        paddingX: '$one',
        textAlign: 'center',
      }}
    >
      <ParagraphText>{formatMessage({ id: titleKey })}</ParagraphText>
      {emptyStateHintKey && (
        <ParagraphText emphasis="subtle" css={{ maxWidth: '36rem' }}>
          {formatMessage({ id: emptyStateHintKey })}
        </ParagraphText>
      )}
      <Button
        id={addRuleButtonId}
        priority="primary"
        onClick={onAddRule}
        disabled={!enabled}
      >
        {formatMessage({
          id: addRuleLabelKey ?? 'CONDITIONAL_ACTION_ADD_RULE_LABEL',
        })}
      </Button>
      {actionType === 'goTo' && (
        <Box
          css={{
            borderTop:
              'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
            width: '60%',
            marginTop: '$one',
            paddingTop: '$one',
          }}
        >
          <ParagraphText emphasis="subtle">
            {formatMessage({ id: 'CONDITIONAL_ACTION_LINEAR_NEXT_NOTE' })}
          </ParagraphText>
        </Box>
      )}
    </StackLayout>
  );
};
